import React, { useState, useEffect, useMemo, useRef } from 'react';
import ExcelTable from './ExcelTable';
import { tableAPI } from '../services/api';
import './Table.css';

const RETURN_STEPS = [1, 2, 3, 4, 5];

const buildInitialHeaders = () => {
  const headers = [
    'S no',
    'Name',
    'Code',
    'Record',
    'Received Date',
    'Received Amt',
    'Charges',
    'Total Returnable',
  ];

  RETURN_STEPS.forEach((step) => {
    headers.push(`Return ${step} Amount`, `Return ${step} Date`, `Balance After R${step}`);
  });

  headers.push('Final Return Amount', 'Final Return Date', 'Balance', 'Remarks', 'Notes / Remarks');
  return [headers];
};

const parseAmount = (value) => {
  if (value === null || value === undefined) return 0;
  if (typeof value === 'number') return value || 0;
  const cleaned = String(value).replace(/,/g, '').replace(/[^0-9.-]/g, '');
  const parsed = parseFloat(cleaned);
  return isNaN(parsed) ? 0 : parsed;
};

const Table7 = () => {
  const savedNames = JSON.parse(localStorage.getItem('defaultTableNames') || '{}');
  const [tableName, setTableName] = useState(savedNames[7] || 'Money Tracker');
  const [isEditingName, setIsEditingName] = useState(false);
  const nameInputRef = useRef(null);
  const [activeTab, setActiveTab] = useState('moneyin'); // 'moneyin' or 'moneyout'
  const [moneyInDataForStats, setMoneyInDataForStats] = useState(null);
  const [moneyOutDataForStats, setMoneyOutDataForStats] = useState(null);

  const initialData = useMemo(() => buildInitialHeaders(), []);

  // Remove dropdown columns - Name and Code should be regular editable cells with auto-population
  // The ExcelTable component already handles auto-population when Code or Name is entered
  const customDropdownColumns = {};

  const currencyColumns = [];
  const totalColumns = [];

  // Keep table name in sync across tabs
  useEffect(() => {
    const handleNameUpdate = () => {
      const saved = JSON.parse(localStorage.getItem('defaultTableNames') || '{}');
      const newName = saved[7] || 'Money Tracker';
      setTableName((prev) => (prev !== newName ? newName : prev));
    };

    window.addEventListener('defaultTableNameUpdated', handleNameUpdate);
    return () => {
      window.removeEventListener('defaultTableNameUpdated', handleNameUpdate);
    };
  }, []);

  useEffect(() => {
    if (isEditingName && nameInputRef.current) {
      nameInputRef.current.focus();
      nameInputRef.current.select();
    }
  }, [isEditingName]);

  const handleNameDoubleClick = () => setIsEditingName(true);

  const handleNameBlur = () => {
    setIsEditingName(false);
    const saved = JSON.parse(localStorage.getItem('defaultTableNames') || '{}');
    saved[7] = tableName;
    localStorage.setItem('defaultTableNames', JSON.stringify(saved));
    window.dispatchEvent(new Event('defaultTableNameUpdated'));
  };

  const handleNameKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleNameBlur();
    } else if (e.key === 'Escape') {
      setIsEditingName(false);
    }
  };

  // Load data for stats (with interval refresh) - Money In
  useEffect(() => {
    let isMounted = true;

    const fetchMoneyInData = async () => {
      try {
        const mysqlData = await tableAPI.getById('table7_moneyin');
        if (!isMounted) return;

        if (mysqlData && mysqlData.length > 0) {
          setMoneyInDataForStats(mysqlData);
          localStorage.setItem('table_table7_moneyin', JSON.stringify(mysqlData));
        } else {
          localStorage.removeItem('table_table7_moneyin');
          setMoneyInDataForStats(null);
        }
      } catch (error) {
        console.error('Error loading money in table data:', error);
        if (!isMounted) return;
        const localData = localStorage.getItem('table_table7_moneyin');
        if (localData) {
          try {
            setMoneyInDataForStats(JSON.parse(localData));
          } catch {
            setMoneyInDataForStats(null);
          }
        } else {
          setMoneyInDataForStats(null);
        }
      }
    };

    fetchMoneyInData();
    const interval = setInterval(fetchMoneyInData, 2000);

    return () => {
      isMounted = false;
      clearInterval(interval);
    };
  }, []);

  // Load data for stats (with interval refresh) - Money Out
  useEffect(() => {
    let isMounted = true;

    const fetchMoneyOutData = async () => {
      try {
        const mysqlData = await tableAPI.getById('table7_moneyout');
        if (!isMounted) return;

        if (mysqlData && mysqlData.length > 0) {
          setMoneyOutDataForStats(mysqlData);
          localStorage.setItem('table_table7_moneyout', JSON.stringify(mysqlData));
        } else {
          localStorage.removeItem('table_table7_moneyout');
          setMoneyOutDataForStats(null);
        }
      } catch (error) {
        console.error('Error loading money out table data:', error);
        if (!isMounted) return;
        const localData = localStorage.getItem('table_table7_moneyout');
        if (localData) {
          try {
            setMoneyOutDataForStats(JSON.parse(localData));
          } catch {
            setMoneyOutDataForStats(null);
          }
        } else {
          setMoneyOutDataForStats(null);
        }
      }
    };

    fetchMoneyOutData();
    const interval = setInterval(fetchMoneyOutData, 2000);

    return () => {
      isMounted = false;
      clearInterval(interval);
    };
  }, []);

  // Calculate stats for a given data set
  const calculateStats = (dataForStats) => {
    if (!dataForStats || dataForStats.length < 2) {
      return {
        totalReturnable: 0,
        outstanding: 0,
        totalCollected: 0,
        totalReceived: 0,
        totalCharges: 0,
        cleared: 0,
        pending: 0,
        notStarted: 0,
      };
    }

    const headers = dataForStats[0];
    const totalReturnableIndex = headers.indexOf('Total Returnable');
    const balanceIndex = headers.indexOf('Balance');
    const receivedIndex = headers.indexOf('Received Amt');
    const chargesIndex = headers.indexOf('Charges');
    const remarksIndex = headers.indexOf('Remarks');

    const totals = {
      totalReturnable: 0,
      outstanding: 0,
      totalCollected: 0,
      totalReceived: 0,
      totalCharges: 0,
      cleared: 0,
      pending: 0,
      notStarted: 0,
    };

    dataForStats.slice(1).forEach((row) => {
      if (!row || row.length === 0) return;
      const hasData = row.some(
        (cell, idx) =>
          idx > 0 && cell !== '' && cell !== null && cell !== undefined
      );
      if (!hasData) return;

      const totalReturnable =
        totalReturnableIndex !== -1
          ? parseAmount(row[totalReturnableIndex])
          : 0;
      const balance =
        balanceIndex !== -1 ? parseAmount(row[balanceIndex]) : totalReturnable;
      const received =
        receivedIndex !== -1 ? parseAmount(row[receivedIndex]) : 0;
      const charges =
        chargesIndex !== -1 ? parseAmount(row[chargesIndex]) : 0;
      const remarks = remarksIndex !== -1 ? String(row[remarksIndex] || '').toLowerCase() : '';

      totals.totalReturnable += totalReturnable;
      totals.outstanding += balance;
      totals.totalReceived += received;
      totals.totalCharges += charges;

      if (remarks === 'cleared' || Math.abs(balance) < 0.01) {
        totals.cleared += 1;
      } else if (remarks === 'pending') {
        totals.pending += 1;
      } else {
        totals.notStarted += 1;
      }
    });

    totals.totalCollected = totals.totalReturnable - totals.outstanding;
    return totals;
  };

  const moneyInStats = useMemo(() => calculateStats(moneyInDataForStats), [moneyInDataForStats]);
  const moneyOutStats = useMemo(() => calculateStats(moneyOutDataForStats), [moneyOutDataForStats]);

  // Get stats for active tab
  const activeStats = activeTab === 'moneyin' ? moneyInStats : moneyOutStats;

  const formatCurrency = (value) =>
    `₹${Number(value || 0).toLocaleString('en-IN', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;

  return (
    <div className="table-page">
      <div className="table-header">
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', width: '100%' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '24px', flexWrap: 'wrap' }}>
            {isEditingName ? (
              <input
                ref={nameInputRef}
                type="text"
                value={tableName}
                onChange={(e) => setTableName(e.target.value)}
                onBlur={handleNameBlur}
                onKeyDown={handleNameKeyDown}
                className="table-name-input"
                maxLength={50}
              />
            ) : (
              <h1
                onDoubleClick={handleNameDoubleClick}
                className="table-name-editable"
                title="Double-click to edit"
                style={{ margin: 0 }}
              >
                {tableName}
                <span className="edit-icon">✎</span>
              </h1>
            )}
            <div
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: '24px',
                flexWrap: 'wrap',
                marginLeft: 'auto',
              }}
            >
              <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                <span style={{ fontSize: '0.75rem', color: '#6b7280', fontWeight: '500' }}>
                  Outstanding Balance:
                </span>
                <span style={{ fontSize: '0.875rem', color: '#dc2626', fontWeight: '700' }}>
                  {formatCurrency(activeStats.outstanding)}
                </span>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                <span style={{ fontSize: '0.75rem', color: '#6b7280', fontWeight: '500' }}>
                  Total Returnable:
                </span>
                <span style={{ fontSize: '0.875rem', color: '#2563eb', fontWeight: '700' }}>
                  {formatCurrency(activeStats.totalReturnable)}
                </span>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                <span style={{ fontSize: '0.75rem', color: '#6b7280', fontWeight: '500' }}>
                  Collected:
                </span>
                <span style={{ fontSize: '0.875rem', color: '#16a34a', fontWeight: '700' }}>
                  {formatCurrency(activeStats.totalCollected)}
                </span>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                <span style={{ fontSize: '0.75rem', color: '#6b7280', fontWeight: '500' }}>
                  Cleared / Pending / Not yet:
                </span>
                <span style={{ fontSize: '0.875rem', color: '#374151', fontWeight: '700' }}>
                  {activeStats.cleared} / {activeStats.pending} / {activeStats.notStarted}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div style={{ 
        display: 'flex', 
        gap: '8px', 
        marginBottom: '24px',
        borderBottom: '2px solid #e5e7eb',
        paddingBottom: '0'
      }}>
        <button
          onClick={() => setActiveTab('moneyin')}
          style={{
            padding: '12px 24px',
            backgroundColor: activeTab === 'moneyin' ? '#3b82f6' : 'transparent',
            color: activeTab === 'moneyin' ? 'white' : '#6b7280',
            border: 'none',
            borderBottom: activeTab === 'moneyin' ? '3px solid #3b82f6' : '3px solid transparent',
            cursor: 'pointer',
            fontSize: '16px',
            fontWeight: activeTab === 'moneyin' ? '600' : '400',
            transition: 'all 0.2s',
            borderRadius: '8px 8px 0 0',
            marginBottom: '-2px'
          }}
          onMouseOver={(e) => {
            if (activeTab !== 'moneyin') {
              e.target.style.backgroundColor = '#f3f4f6';
            }
          }}
          onMouseOut={(e) => {
            if (activeTab !== 'moneyin') {
              e.target.style.backgroundColor = 'transparent';
            }
          }}
        >
          Money In
        </button>
        <button
          onClick={() => setActiveTab('moneyout')}
          style={{
            padding: '12px 24px',
            backgroundColor: activeTab === 'moneyout' ? '#3b82f6' : 'transparent',
            color: activeTab === 'moneyout' ? 'white' : '#6b7280',
            border: 'none',
            borderBottom: activeTab === 'moneyout' ? '3px solid #3b82f6' : '3px solid transparent',
            cursor: 'pointer',
            fontSize: '16px',
            fontWeight: activeTab === 'moneyout' ? '600' : '400',
            transition: 'all 0.2s',
            borderRadius: '8px 8px 0 0',
            marginBottom: '-2px'
          }}
          onMouseOver={(e) => {
            if (activeTab !== 'moneyout') {
              e.target.style.backgroundColor = '#f3f4f6';
            }
          }}
          onMouseOut={(e) => {
            if (activeTab !== 'moneyout') {
              e.target.style.backgroundColor = 'transparent';
            }
          }}
        >
          Money Out
        </button>
      </div>

      {/* Table for active tab */}
      {activeTab === 'moneyin' ? (
        <ExcelTable
          initialData={initialData}
          currencyColumns={currencyColumns}
          customDropdownColumns={customDropdownColumns}
          fixedHeaders={true}
          totalColumns={totalColumns}
          tableId="table7_moneyin"
          tableName={`${tableName} - Money In`}
        />
      ) : (
        <ExcelTable
          initialData={initialData}
          currencyColumns={currencyColumns}
          customDropdownColumns={customDropdownColumns}
          fixedHeaders={true}
          totalColumns={totalColumns}
          tableId="table7_moneyout"
          tableName={`${tableName} - Money Out`}
        />
      )}
    </div>
  );
};

export default Table7;
